import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Row, Col } from '@saucelabs/sl-component-grid';
import { IconWarningFill, IconCloseFill } from '@saucelabs/ds-icons';
import { OverlayTrigger, Tooltip } from '@saucelabs/sl-component-overlay';

import { parseMetricsValue, isRegressionDetectedMetrics } from './../utils';
import { METRICS_INFO, JANK_METRICS_INFO, PERFORMANCE_ERROR_CAPTION, REGRESSION_CONTENT } from './../constants';

const PerformanceInfoV2 = ({
  performanceData,
  getPerformanceURL,
  regressionData,
  onLinkClick,
  isAdvanceDisabled,
}) => {
  const {
    error,
    value: performanceValue,
    url,
    orderIndex,
  } = performanceData;
  const metricsInfo = performanceData.type !== 'scroll' ? METRICS_INFO : JANK_METRICS_INFO;

  const regressionWarning = () => {
    const { is_regression_detected: isRegression, regressionType } = regressionData;

    if (!isRegression) {
      return null;
    }

    return !regressionType ? <IconWarningFill className="w-2 h-2 fill-current text-orange-600 mr-1" /> : (
      <OverlayTrigger
        overlay={(
          <Tooltip
            id={`${orderIndex}-perf-warning-tooltip`}
            placement="left"
          >
            {REGRESSION_CONTENT.getIn([regressionType, 'tooltip'])}
          </Tooltip>
        )}
        placement="top"
      >
        <IconWarningFill className="w-2 h-2 fill-current text-orange-600 mr-1" />
      </OverlayTrigger>
    );
  };

  return (
    <section className="border border-solid border-gray-300 bg-white rounded p-2">
      <div className="flex text-high-emphasis leading-relaxed">
        <div className="flex items-center">
          {regressionData && regressionWarning()}
          <span className="font-bold">URL:&nbsp;</span>
          {url}
        </div>
        <div className="ml-auto flex cursor-pointer">
          {
            (isAdvanceDisabled || error) ? (
              <a
                onClick={() => onLinkClick(true)}
                role="presentation"
              >
                View Report
              </a>
            ) : (
              <Link
                to={getPerformanceURL({ orderIndex }).reportPageURL}
              >
                View Report
              </Link>
            )
          }
        </div>
      </div>

      <div className="leading-relaxed mt-0.5 text-high-emphasis">
        {error ? (
          <div className="flex items-center">
            <IconCloseFill className="w-2 h-2 fill-current text-red-900" />
            <div className="ml-1">
              {PERFORMANCE_ERROR_CAPTION}
            </div>
          </div>
        ) : (
          <div>
            <Row>
              {
                metricsInfo.entrySeq().map(([key, value]) => (
                  <Col
                    key={value}
                    sm={6}
                    xs={12}
                  >
                    <span className="font-bold">{`${value}: `}</span>
                    <span className={classNames(
                      { 'text-orange-600': isRegressionDetectedMetrics(regressionData, key) })}
                    >
                      {parseMetricsValue(value === metricsInfo.get('score') ?
                        performanceData[key] : performanceValue.metrics[key], value)}
                    </span>
                  </Col>
                ))
              }
            </Row>
          </div>
        )}
      </div>
    </section>
  );
};

PerformanceInfoV2.propTypes = {
  getPerformanceURL: PropTypes.func.isRequired,
  isAdvanceDisabled: PropTypes.bool.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  performanceData: PropTypes.object.isRequired,
  regressionData: PropTypes.object,
};

PerformanceInfoV2.defaultProps = {
  regressionData: null,
};

export default PerformanceInfoV2;
